import {useMemo} from "react";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {PhoneIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Highlight, Text} from "@pg-design/text-module";
import {areaFormat, numberFormat, phoneNumberFormat, pluralize, roomsFormat} from "@pg-mono/string-utils";
import {getRandomConsultant} from "@pg-rp/consultants";

import {ITinderMultiLeadSentItem} from "../../types/tinder_multi_lead/ITinderMultiLeadState";
import {hitTinderMultiLeadSummaryGtmEvent} from "./tracking/hit_tinder_multi_lead_gtm_events";
import {getTinderMultiLeadOfferTypeName} from "./tinder_multi_lead_utils";

interface IProps {
    onClose: () => void;
    sentApplications: ITinderMultiLeadSentItem[];
}

const CONSULTANT_PHONE_NUMBER = "48 734440816";
const CONSULTANT_PHONE_HREF = `tel:+${CONSULTANT_PHONE_NUMBER.replace(/\D/g, "")}`;

const inquiryPluralize = pluralize(["zapytanie", "zapytania", "zapytań"]);

export function TinderMultiLeadSummary({onClose, sentApplications}: IProps) {
    const consultant = useMemo(() => getRandomConsultant(), []);
    const vendorNames = Array.from(new Set(sentApplications.map((item) => item.vendorName)));

    const handleConsultantClick = () => {
        hitTinderMultiLeadSummaryGtmEvent({action: "click", clickText: phoneNumberFormat(CONSULTANT_PHONE_NUMBER), offersCount: sentApplications.length});
    };

    const handleReturnClick = () => {
        hitTinderMultiLeadSummaryGtmEvent({action: "click", clickText: "Powróć do poszukiwań", offersCount: sentApplications.length});
        onClose();
    };

    return (
        <div className={summaryStyle}>
            <div className={summaryHeaderStyle}>
                <Text variant="headline_3" as="h2" color="var(--colors-secondary)">
                    <Highlight>
                        Wysłałeś łącznie {sentApplications.length} {inquiryPluralize(sentApplications.length)}
                    </Highlight>
                </Text>
                <Text variant="body_copy_2" color="var(--colors-gray-800)">
                    Spodziewaj się kontaktu od {vendorNames.length ? vendorNames.join(", ") : "deweloperów"}
                </Text>
            </div>

            <div className={sentListStyle}>
                {sentApplications.map((item) => (
                    <div className={sentItemStyle} key={item.applicationUuid}>
                        {item.image ? <img src={item.image} alt={item.offerName} className={sentImageStyle} /> : <div className={sentImageFallbackStyle} />}
                        <div className={sentContentStyle}>
                            <div className={sentDetailsStyle}>
                                <div>
                                    <Text variant="body_copy_1" color="var(--colors-secondary)" className={sentTitleStyle}>
                                        {getTinderMultiLeadOfferTypeName(item.offerType)}, {roomsFormat(item.rooms)}, {areaFormat(item.area)}
                                    </Text>
                                    <Text variant="info_txt_3" color="var(--colors-gray-800)">
                                        {item.vendorName}
                                    </Text>
                                </div>
                                <Text variant="info_txt_2" color="var(--colors-gray-700)" className={sentAddressStyle}>
                                    {[item.offerName, item.address].filter(Boolean).join(", ")}
                                </Text>
                            </div>

                            <Text as="div" variant="headline_3" strong color="var(--colors-secondary)" className={sentPriceStyle}>
                                <Highlight>
                                    {item.price ? (
                                        <>
                                            {numberFormat(item.price)} <span className={sentPriceUnitStyle}>zł</span>
                                        </>
                                    ) : (
                                        "Cena u dewelopera"
                                    )}
                                </Highlight>
                            </Text>
                        </div>
                    </div>
                ))}
            </div>

            <a className={consultantLinkStyle} href={CONSULTANT_PHONE_HREF} onClick={handleConsultantClick}>
                <div className={consultantStyle}>
                    <div className={consultantMainStyle}>
                        <Image
                            className={consultantImageWrapperStyle}
                            imageClassName={consultantImageStyle}
                            width="72"
                            height="72"
                            src={consultant.image.s144}
                            alt={consultant.fullName}
                        />
                        <div className={consultantTextStyle}>
                            <div className={consultantHeadingStyle}>
                                <Text variant="headline_5" strong color="var(--colors-secondary)">
                                    Potrzebujesz wsparcia w poszukiwaniu mieszkania?
                                </Text>
                                <Text variant="body_copy_2" color="var(--colors-gray-700)">
                                    Zadzwoń, a pomogę Ci znaleźć mieszkanie dla Ciebie.
                                </Text>
                            </div>
                            <Text variant="info_txt_1" color="var(--colors-secondary)">
                                {consultant.fullName}, Konsultant rynekpierwotny.pl
                            </Text>
                        </div>
                    </div>
                    <div className={consultantPhoneStyle}>
                        <span className={consultantPhoneIconStyle} aria-hidden>
                            <PhoneIcon size="1.8" fill="var(--colors-secondary)" />
                        </span>
                        <Text variant="body_copy_0" strong color="var(--colors-secondary)">
                            {phoneNumberFormat(CONSULTANT_PHONE_NUMBER)}
                        </Text>
                    </div>
                </div>
            </a>

            <Button type="button" variant="filled_primary" fullWidth onClick={handleReturnClick} className={buttonStyle}>
                Powróć do poszukiwań
            </Button>
        </div>
    );
}

const summaryStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2.5,
    overflowY: "auto",
    p: 2,
    md: {
        gap: 3,
        p: 3
    }
});

const summaryHeaderStyle = css({
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    gap: 1,
    pr: 4,
    md: {
        pr: 0
    }
});

const sentListStyle = css({
    display: "grid",
    flexShrink: 0,
    gap: 1
});

const sentItemStyle = css({
    display: "grid",
    gridTemplateColumns: "112px minmax(0, 1fr)",
    gap: 2,
    alignItems: "center",
    minHeight: 112,
    overflow: "hidden",
    borderRadius: 2,
    backgroundColor: "var(--colors-white)",
    boxShadow: "var(--shadow-2)",
    md: {
        gridTemplateColumns: "154px minmax(0, 1fr)",
        height: 128
    }
});

const sentImageStyle = css({
    width: 112,
    height: "100%",
    objectFit: "cover",
    alignSelf: "stretch",
    md: {
        width: 154
    }
});

const sentImageFallbackStyle = css({
    width: 112,
    height: "100%",
    alignSelf: "stretch",
    backgroundColor: "var(--colors-gray-300)",
    md: {
        width: 154
    }
});

const sentContentStyle = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignSelf: "stretch",
    minWidth: 0,
    py: 1,
    pr: 1.5
});

const sentDetailsStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    minWidth: 0
});

const sentTitleStyle = css({
    textTransform: "capitalize"
});

const sentAddressStyle = css({
    maxWidth: "46.5rem"
});

const sentPriceStyle = css({
    fontSize: "2.162rem",
    lineHeight: "2.8rem"
});

const sentPriceUnitStyle = css({
    fontSize: "1.2rem",
    lineHeight: "1.8rem"
});

const consultantLinkStyle = css({
    flexShrink: 0,
    color: "inherit",
    textDecoration: "none"
});

const consultantStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
    p: 2,
    borderRadius: 2,
    backgroundColor: "var(--colors-gray-100)",
    border: "1px solid var(--colors-gray-300)",
    md: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2
    }
});

const consultantMainStyle = css({
    display: "flex",
    gap: 2,
    alignItems: "center",
    minWidth: 0
});

const consultantImageWrapperStyle = css({
    flexShrink: 0,
    borderRadius: "100%"
});

const consultantImageStyle = css({
    borderRadius: "100%"
});

const consultantTextStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
    minWidth: 0
});

const consultantHeadingStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 0.5
});

const consultantPhoneStyle = css({
    display: "flex",
    gap: 1,
    alignItems: "center",
    flexShrink: 0
});

const consultantPhoneIconStyle = css({
    display: "inline-flex",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    width: "3.6rem",
    height: "3.6rem",
    borderRadius: "100%",
    backgroundColor: "var(--colors-primary)"
});

const buttonStyle = css({
    flexShrink: 0
});
import {useMemo} from "react";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {PhoneIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Highlight, Text} from "@pg-design/text-module";
import {areaFormat, numberFormat, phoneNumberFormat, pluralize, roomsFormat} from "@pg-mono/string-utils";
import {getRandomConsultant} from "@pg-rp/consultants";

import {ITinderMultiLeadSentItem} from "../../types/tinder_multi_lead/ITinderMultiLeadState";
import {hitTinderMultiLeadSummaryGtmEvent} from "./tracking/hit_tinder_multi_lead_gtm_events";
import {getTinderMultiLeadOfferTypeName} from "./tinder_multi_lead_utils";

interface IProps {
    onClose: () => void;
    sentApplications: ITinderMultiLeadSentItem[];
}

const CONSULTANT_PHONE_NUMBER = "48 734440816";
const CONSULTANT_PHONE_HREF = `tel:+${CONSULTANT_PHONE_NUMBER.replace(/\D/g, "")}`;

const inquiryPluralize = pluralize(["zapytanie", "zapytania", "zapytań"]);

export function TinderMultiLeadSummary({onClose, sentApplications}: IProps) {
    const consultant = useMemo(() => getRandomConsultant(), []);
    const vendorNames = Array.from(new Set(sentApplications.map((item) => item.vendorName)));

    const handleConsultantClick = () => {
        hitTinderMultiLeadSummaryGtmEvent({action: "click", clickText: phoneNumberFormat(CONSULTANT_PHONE_NUMBER), offersCount: sentApplications.length});
    };

    const handleReturnClick = () => {
        hitTinderMultiLeadSummaryGtmEvent({action: "click", clickText: "Powróć do poszukiwań", offersCount: sentApplications.length});
        onClose();
    };

    return (
        <div className={summaryStyle}>
            <div className={summaryHeaderStyle}>
                <Text variant="headline_3" as="h2" color="var(--colors-secondary)">
                    <Highlight>
                        Wysłałeś łącznie {sentApplications.length} {inquiryPluralize(sentApplications.length)}
                    </Highlight>
                </Text>
                <Text variant="body_copy_2" color="var(--colors-gray-800)">
                    Spodziewaj się kontaktu od {vendorNames.length ? vendorNames.join(", ") : "deweloperów"}
                </Text>
            </div>

            <div className={sentListStyle}>
                {sentApplications.map((item) => (
                    <div className={sentItemStyle} key={item.applicationUuid}>
                        {item.image ? <img src={item.image} alt={item.offerName} className={sentImageStyle} /> : <div className={sentImageFallbackStyle} />}
                        <div className={sentContentStyle}>
                            <div className={sentDetailsStyle}>
                                <div>
                                    <Text variant="body_copy_1" color="var(--colors-secondary)" className={sentTitleStyle}>
                                        {getTinderMultiLeadOfferTypeName(item.offerType)}, {roomsFormat(item.rooms)}, {areaFormat(item.area)}
                                    </Text>
                                    <Text variant="info_txt_3" color="var(--colors-gray-800)">
                                        {item.vendorName}
                                    </Text>
                                </div>
                                <Text variant="info_txt_2" color="var(--colors-gray-700)" className={sentAddressStyle}>
                                    {[item.offerName, item.address].filter(Boolean).join(", ")}
                                </Text>
                            </div>

                            <Text as="div" variant="headline_3" strong color="var(--colors-secondary)" className={sentPriceStyle}>
                                <Highlight>
                                    {item.price ? (
                                        <>
                                            {numberFormat(item.price)} <span className={sentPriceUnitStyle}>zł</span>
                                        </>
                                    ) : (
                                        "Cena u dewelopera"
                                    )}
                                </Highlight>
                            </Text>
                        </div>
                    </div>
                ))}
            </div>

            <a className={consultantLinkStyle} href={CONSULTANT_PHONE_HREF} onClick={handleConsultantClick}>
                <div className={consultantStyle}>
                    <div className={consultantMainStyle}>
                        <Image
                            className={consultantImageWrapperStyle}
                            imageClassName={consultantImageStyle}
                            width="72"
                            height="72"
                            src={consultant.image.s144}
                            alt={consultant.fullName}
                        />
                        <div className={consultantTextStyle}>
                            <div className={consultantHeadingStyle}>
                                <Text variant="headline_5" strong color="var(--colors-secondary)">
                                    Potrzebujesz wsparcia w poszukiwaniu mieszkania?
                                </Text>
                                <Text variant="body_copy_2" color="var(--colors-gray-700)">
                                    Zadzwoń, a pomogę Ci znaleźć mieszkanie dla Ciebie.
                                </Text>
                            </div>
                            <Text variant="info_txt_1" color="var(--colors-secondary)">
                                {consultant.fullName}, Konsultant rynekpierwotny.pl
                            </Text>
                        </div>
                    </div>
                    <div className={consultantPhoneStyle}>
                        <span className={consultantPhoneIconStyle} aria-hidden>
                            <PhoneIcon size="1.8" fill="var(--colors-secondary)" />
                        </span>
                        <Text variant="body_copy_0" strong color="var(--colors-secondary)">
                            {phoneNumberFormat(CONSULTANT_PHONE_NUMBER)}
                        </Text>
                    </div>
                </div>
            </a>

            <Button type="button" variant="filled_primary" fullWidth onClick={handleReturnClick} className={buttonStyle}>
                Powróć do poszukiwań
            </Button>
        </div>
    );
}

const summaryStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2.5,
    overflowY: "auto",
    p: 2,
    md: {
        gap: 3,
        p: 3
    }
});

const summaryHeaderStyle = css({
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    gap: 1,
    pr: 4,
    md: {
        pr: 0
    }
});

const sentListStyle = css({
    display: "grid",
    flexShrink: 0,
    gap: 1
});

const sentItemStyle = css({
    display: "grid",
    gridTemplateColumns: "112px minmax(0, 1fr)",
    gap: 2,
    alignItems: "center",
    minHeight: 112,
    overflow: "hidden",
    borderRadius: 2,
    backgroundColor: "var(--colors-white)",
    boxShadow: "var(--shadow-2)",
    md: {
        gridTemplateColumns: "154px minmax(0, 1fr)",
        height: 128
    }
});

const sentImageStyle = css({
    width: 112,
    height: "100%",
    objectFit: "cover",
    alignSelf: "stretch",
    md: {
        width: 154
    }
});

const sentImageFallbackStyle = css({
    width: 112,
    height: "100%",
    alignSelf: "stretch",
    backgroundColor: "var(--colors-gray-300)",
    md: {
        width: 154
    }
});

const sentContentStyle = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignSelf: "stretch",
    minWidth: 0,
    py: 1,
    pr: 1.5
});

const sentDetailsStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    minWidth: 0
});

const sentTitleStyle = css({
    textTransform: "capitalize"
});

const sentAddressStyle = css({
    maxWidth: "46.5rem"
});

const sentPriceStyle = css({
    fontSize: "2.162rem",
    lineHeight: "2.8rem"
});

const sentPriceUnitStyle = css({
    fontSize: "1.2rem",
    lineHeight: "1.8rem"
});

const consultantLinkStyle = css({
    flexShrink: 0,
    color: "inherit",
    textDecoration: "none"
});

const consultantStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
    p: 2,
    borderRadius: 2,
    backgroundColor: "var(--colors-gray-100)",
    border: "1px solid var(--colors-gray-300)",
    md: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2
    }
});

const consultantMainStyle = css({
    display: "flex",
    gap: 2,
    alignItems: "center",
    minWidth: 0
});

const consultantImageWrapperStyle = css({
    flexShrink: 0,
    borderRadius: "100%"
});

const consultantImageStyle = css({
    borderRadius: "100%"
});

const consultantTextStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
    minWidth: 0
});

const consultantHeadingStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 0.5
});

const consultantPhoneStyle = css({
    display: "flex",
    gap: 1,
    alignItems: "center",
    flexShrink: 0
});

const consultantPhoneIconStyle = css({
    display: "inline-flex",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    width: "3.6rem",
    height: "3.6rem",
    borderRadius: "100%",
    backgroundColor: "var(--colors-primary)"
});

const buttonStyle = css({
    flexShrink: 0
});
